import { Button } from "@shared/components";
import Input from "@shared/components/input/Input";
import { BUTTON_SIZE } from "@shared/components/button/types";
import { INPUT_SIZE } from "@shared/components/input";
import DocumentVerifiedOutlineIcon from "@svg/new-design/document-verfied-outline";
import InfoCircleIcon from "@svg/new-design/info-circle";
import { ChangeEvent, FC, useState } from "react";
import ArrowVector from "../../../assets/patterns/ArrowVector";
import classNames from "classnames";
import IconCheckmark from "@svg/new-design/checkmark";
import examApi from "@api/exam";
import { CERTIFICATE_LANG } from "enums/certificate";

const certificateImage =
  "https://faradars.org/wp-content/uploads/2025/10/04/certificate.png";

const VerifyCertificateSection: FC = () => {
  const [certificateNumber, setCertificateNumber] = useState("");
  const [isInputTouched, setIsInputTouched] = useState(false);
  const [persianCharError, setPersianCharError] = useState(false);

  const [result, setResult] = useState<{
    message: string;
    is_valid: boolean;
  } | null>(null);

  const handleCheckCertificate = async () => {
    if (!certificateNumber) return;
    try {
      const res = await examApi
        .showCertificate(certificateNumber, CERTIFICATE_LANG.FA)
        .fetch();
      if (res.status && res.data?.hash && res?.data?.created_at) {
        setResult({
          message: `گواهینامه با شماره ${res.data.hash} در تاریخ ${res.data.created_at} در فرادرس صادر شده است. `,
          is_valid: true,
        });
      } else {
        setResult({
          message: "شماره گواهینامه وارد شده معتبر نیست.",
          is_valid: false,
        });
      }
    } catch (err: any) {
      if (err?.error?.code === 404) {
        setResult({
          message: "شماره گواهینامه وارد شده معتبر نیست.",
          is_valid: false,
        });
      } else {
        setResult({
          message:
            err?.error?.message || "شماره گواهینامه وارد شده معتبر نیست.",
          is_valid: false,
        });
      }
    }
  };

  const inputOnChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length > 8) return;
    const persianRegex = /[\u0600-\u06FF]/;
    const hasPersianChars = persianRegex.test(value);
    setPersianCharError(hasPersianChars);
    const filteredValue = value.replace(/[^a-zA-Z0-9]/g, "");
    setCertificateNumber(filteredValue);
    if (result) setResult(null);
    if (!hasPersianChars) setPersianCharError(false);
  };

  return (
    <div className="section-container grid grid-cols-12 gap-4     relative bottom-fds-60 md:bottom-20 mx-auto px-4    ">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleCheckCertificate();
        }}
        className="col-span-12 lg:col-span-8 bg-fds-primary rounded-12 p-6  md:p-12 flex flex-col w-full gap-3  md:gap-5    relative shadow-md"
      >
        <div className="flex flex-col gap-2  w-full">
          <div className="flex items-center gap-2">
            <DocumentVerifiedOutlineIcon className="text-fds-icon-secondary w-6  md:w-8" />
            <p className="title-md-medium md:title-lg text-fds-primary">
              بررسی اعتبار گواهینامه
            </p>
          </div>
          <p className="text-fds-secondary body-md md:body-lg">
            با وارد کردن شماره گواهینامه، از اعتبار آن اطمینان حاصل کنید.
          </p>
          <Input
            placeholder="شماره گواهینامه"
            name="certificateNumber"
            value={certificateNumber}
            onChange={inputOnChangeHandler}
            required={false}
            size={INPUT_SIZE.LARGE}
            error={Boolean(result && !result?.is_valid)}
            successMessage={Boolean(result && result?.is_valid)}
            touched={isInputTouched}
            onBlur={() => setIsInputTouched(true)}
            inputClassName={classNames("outline-fds-success")}
          />
          {persianCharError && (
            <div className="flex items-center gap-2  w-full justify-start text-fds-danger">
              <p className="text-inherit body-sm">
                لطفاً فقط اعداد و حروف انگلیسی وارد کنید
              </p>
            </div>
          )}
          {result && (
            <div
              className={classNames(
                "flex items-start gap-2  w-full justify-start",
                result.is_valid ? "text-fds-success" : "text-fds-danger"
              )}
            >
              {result.is_valid ? (
                <IconCheckmark className="min-w-5 w-5 h-5 text-inherit mt-0.5" />
              ) : null}
              <p className="text-inherit body-sm">{result.message}</p>
            </div>
          )}
        </div>
        <Button
          size={BUTTON_SIZE.LARGE}
          className="mt-3  md:mt-0"
          disabled={!certificateNumber}
          type="submit"
        >
          تأیید و بررسی
        </Button>
        <div className="items-center gap-2  hidden md:flex">
          <InfoCircleIcon className="text-fds-icon-tertiary w-5 h-5" />
          <p className="body-md text-fds-tertiary">
            شماره گواهینامه را مطابق نمونه تصویر روبرو، میتونین پیدا کنین!
          </p>
        </div>
      </form>
      <div className="hidden lg:block col-span-4 relative h-fit -mt-0.5">
        <ArrowVector className="absolute -right-20 top-[68%] z-40 hidden lg:block rotate-[10deg]" />
        <img
          src={certificateImage}
          alt="certificate sample"
          className="w-full h-auto"
        />
      </div>
    </div>
  );
};

export default VerifyCertificateSection;
